import React, { useState, useEffect } from 'react';
import { syncManager } from '../services/sync';
import { db } from '../db';
import { ArrowLeft, RefreshCw, ExternalLink, Table, FileSpreadsheet, Zap, X, Save } from 'lucide-react';
import CollapsiblePlaybookRow from './CollapsiblePlaybookRow';
import CollapsibleTaskRow from './CollapsibleTaskRow';

// Helper to format cell text (Markdown-lite)
const formatCellContent = (text) => {
    if (typeof text !== 'string') {return text;}

    // Split by newlines
    return text.split('\n').map((line, i) => {
        // Handle Headers (### Header)
        if (line.trim().startsWith('###')) {
            const content = line.trim().replace(/^###\s*/, '');
            return (
                <h3 key={i} className="text-lg font-black text-slate-800 uppercase tracking-tight mt-6 mb-3">
                    {content}
                </h3>
            );
        }

        // Handle Bold (**text**)
        const parts = line.split(/(\*\*.*?\*\*)/g);
        return (
            <div key={i} className={`min-h-[1.2em] ${line.trim().startsWith('-') ? 'pl-4' : ''}`}>
                {parts.map((part, j) => {
                    if (part.startsWith('**') && part.endsWith('**')) {
                        return <strong key={j} className="font-bold text-slate-800">{part.slice(2, -2)}</strong>;
                    }
                    return <span key={j}>{part}</span>;
                })}
            </div>
        );
    });
};

const GoogleSheetsIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5" xmlns="http://www.w3.org/2000/svg">
        <path d="M14 2H6C4.89 2 4 2.9 4 4V20C4 21.1 4.89 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z" fill="#0F9D58" />
        <path d="M14 2V8H20L14 2Z" fill="#B7E1CD" />
        <path d="M8 13H16V14.5H8V13ZM8 16H16V17.5H8V16ZM8 10H16V11.5H8V10Z" fill="white" />
    </svg>
);

const NativeTabTable = ({ tabName, onBack, sheetUrl, onRowClick, targetClientEmail, onNavigate }) => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [syncMessage, setSyncMessage] = useState(null);
    const isActionPlan = tabName === 'My Action Plan' || tabName === 'My_Action_Plan';
    const isPlaybook = tabName.toLowerCase().includes('playbook') || tabName.toLowerCase().includes('content');
    const [editingRow, setEditingRow] = useState(null);
    const [isSaving, setIsSaving] = useState(false);

    // Data Normalization (Harden against n8n raw outputs and variable header offsets)
    const normalizeData = (rawData) => {
        if (!Array.isArray(rawData) || rawData.length === 0) {return rawData;}

        const commonHeaders = [
            'timestamp', 'id', 'task_id', 'content_id', 'area', 'item', 'notes', 'status',
            'value', 'email', 'edit entry', '#ref!', 'date', 'name', 'title', 'body',
            'description', 'category', 'priority', 'status', 'owner', 'link', 'url',
            'client_name', 'userid', 'orgid', 'action', 'type', 'topic', 'goal', 'metric',
            'baseline', 'target', 'timeline', 'action plan', 'budget', 'resources', 'progress'
        ];

        const isHeaderLike = (obj) => {
            const keys = Object.keys(obj).filter(k =>
                k !== 'row_number' && k !== 'Timestamp' && !k.startsWith('_') &&
                !['billingStatus', 'accessStatus', 'sheetUrl'].includes(k)
            );
            if (keys.length === 0) {return false;}

            // Check if values in this object look like headers (Majority vote)
            const matches = keys.filter(k => {
                const val = String(obj[k]).trim().toLowerCase();
                if (!val) {return false;}
                return val === k.toLowerCase() ||
                    commonHeaders.includes(val) ||
                    val.includes('name') || val.includes('date') || val.includes('status') ||
                    val.includes('id') || val.includes('log') || val.includes('task') ||
                    val.includes('goal') || val.includes('metric') || val.includes('type');
            });
            // Lenient threshold: 60% for large sets, 50% for small sets
            const threshold = keys.length <= 3 ? 0.5 : 0.6;
            return keys.length > 0 && (matches.length >= keys.length * threshold);
        };

        const isRawKeys = (obj) => {
            const keys = Object.keys(obj).filter(k =>
                k !== 'row_number' && k !== 'Timestamp' && !k.startsWith('_')
            );
            const rawKeyPatterns = keys.filter(k =>
                (!isNaN(k) && Number.isInteger(Number(k))) ||
                (typeof k === 'string' && k.length <= 2 && k.match(/^[A-Z]{1,2}$/i)) ||
                (typeof k === 'string' && k.toLowerCase().startsWith('column'))
            );
            return keys.length > 0 && (rawKeyPatterns.length >= keys.length * 0.6);
        };

        // Scan the first 5 rows to find a header-like row
        let headerRowIndex = -1;
        for (let i = 0; i < Math.min(rawData.length, 5); i++) {
            if (isHeaderLike(rawData[i])) {
                headerRowIndex = i;
                break;
            }
        }

        // HEURISTIC: If we found a header row deeper than 0, OR row 0 has raw keys
        if (headerRowIndex !== -1 || isRawKeys(rawData[0])) {
            const index = headerRowIndex !== -1 ? headerRowIndex : 0;
            const headerMap = { ...rawData[index] };

            console.log(`[NativeTabTable] Normalizing ${tabName}: Found header at index ${index}`);

            // Special case: if Row 0 is the ACTUAL header, and we have raw keys, 
            // the n8n data already has data in rawData[0].
            // If headerRowIndex is 0, we treat rawData[0] as header and skip it.
            // If headerRowIndex is > 0, we skip everything up to and including it.

            const filteredData = rawData.slice(index + 1).filter(row => {
                const significantKeys = Object.keys(row).filter(k => !k.startsWith('_') && k !== 'row_number' && k !== 'Timestamp');
                if (significantKeys.length === 0) {return false;}
                // Skip if row matches header exactly
                const isMatch = significantKeys.every(k => String(row[k]) === String(headerMap[k]));
                return !isMatch;
            });

            return filteredData.map(row => {
                const normalized = {};
                Object.entries(row).forEach(([key, val]) => {
                    if (key === 'row_number' || key === 'Timestamp' || key.startsWith('_')) {
                        normalized[key] = val;
                    } else {
                        const mappedName = headerMap[key];
                        const finalHeader = (typeof mappedName === 'string' && isNaN(mappedName) && mappedName.length > 1) ? mappedName : key;
                        normalized[finalHeader] = val;
                    }
                });
                return normalized;
            });
        }

        return rawData;
    };

    const loadData = async (forceSync = false, messageFallback = null) => {
        setLoading(true);

        // 1. Try Dexie Cache first (unless forceSync)
        if (!forceSync) {
            const cached = await db.tabData.where('tabName').equals(tabName).first();
            if (cached && Array.isArray(cached.data)) {
                const normalizedCached = normalizeData(cached.data);
                setData(normalizedCached);
                setLastUpdated(cached.lastSync);
                setLoading(false);
                return;
            }
        }

        // 2. Fetch from n8n
        let freshData = await syncManager.fetchTabData(tabName, targetClientEmail);
        if (freshData && Array.isArray(freshData)) {
            freshData = normalizeData(freshData);
        }
        const baseData = Array.isArray(freshData) ? freshData : (data.length > 0 ? data : []);

        // 3. Merge Pending Logs for Optimistic UI
        // Use a safe filter to avoid Dexie index chaining issues causing crashes
        const pending = await db.logs
            .filter(log => log.code === tabName && log.status === 'pending')
            .toArray();
        const merged = [...baseData];

        if (pending.length > 0) {
            pending.forEach(p => {
                try {
                    const parsed = JSON.parse(p.value);
                    const optimistic = {
                        ...parsed,
                        Timestamp: p.timestamp,
                        row_number: p.remoteId,
                        _isPending: true
                    };

                    if (p.remoteId) {
                        // Update existing row (match by row_number if available)
                        const idx = merged.findIndex(r => r.row_number === p.remoteId);
                        if (idx !== -1) {merged[idx] = optimistic;}
                        else {merged.unshift(optimistic);}
                    } else {
                        // New entry
                        merged.unshift(optimistic);
                    }
                } catch (e) {
                    console.error("Error parsing pending log:", e);
                }
            });
        }

        setData(merged);
        if (freshData && Array.isArray(freshData)) {
            setLastUpdated(freshData.lastSync || new Date().toISOString());
        }

        if (messageFallback) {
            setSyncMessage(messageFallback);
            setTimeout(() => setSyncMessage(null), 120000); // Clear after 120s (2 minutes)
        }

        setLoading(false);
    };

    useEffect(() => {
        loadData();
    }, [tabName, targetClientEmail]);

    console.log(`[NativeTabTable] Rendering for ${tabName}, sheetUrl:`, sheetUrl);

    return (
        <div className="flex flex-col h-full animate-in fade-in slide-in-from-right-4 duration-500">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <button onClick={onBack} className="flex items-center gap-2 text-slate-500 hover:text-blue-700 font-black uppercase text-[10px] tracking-widest transition-colors">
                    <ArrowLeft size={16} /> Back to Portal
                </button>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => loadData(true)}
                        disabled={loading}
                        className="p-2 bg-blue-50 text-blue-700 rounded-xl hover:bg-blue-100 transition-all active:scale-95 disabled:opacity-50"
                        title="Refresh Local View (Fast)"
                    >
                        <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
                    </button>

                    {/* Sync Action/Lesson Gate - Explicit Button */}
                    {(tabName.toLowerCase().includes('action plan') || tabName.toLowerCase().includes('playbook') || tabName.toLowerCase().includes('content')) && (
                        <button
                            onClick={async () => {
                                if (loading) {return;}
                                setLoading(true);
                                const result = await syncManager.syncNow();
                                let feedback = "";
                                if (result.success) {
                                    // Use the statusMessage from n8n context if available
                                    if (result.result && result.result.statusMessage) {
                                        feedback = result.result.statusMessage;
                                    } else {
                                        feedback = result.count > 0 ? `Synced ${result.count} local items.` : "Up to date with HQ.";
                                    }
                                } else {
                                    feedback = `Sync Error: ${result.error}`;
                                }
                                await loadData(true, feedback);
                            }}
                            disabled={loading}
                            className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-xl hover:bg-blue-700 transition-all active:scale-95 disabled:opacity-50 text-[10px] font-black uppercase tracking-widest"
                            title="Sync Actions: Remotely update Action Plan and content status"
                        >
                            <Zap size={14} className={loading ? 'animate-pulse' : ''} />
                            {tabName.toLowerCase().includes('playbook') ? 'Sync Lessons' : 'Sync Actions'}
                        </button>
                    )}
                    {sheetUrl && (
                        <a
                            href={sheetUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="p-2 bg-green-50 text-green-700 rounded-xl hover:bg-green-100 transition-all border border-green-100 flex items-center justify-center"
                            title="Open in Google Sheets"
                        >
                            <GoogleSheetsIcon />
                        </a>
                    )}
                </div>
            </div>

            <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-xl overflow-hidden flex flex-col flex-1">
                <div className="p-8 border-b border-slate-50 bg-slate-50/50 flex items-center justify-between">
                    <div>
                        <h2 className="text-2xl font-black text-slate-800 tracking-tight uppercase">{tabName.replace(/_/g, ' ')}</h2>
                        {lastUpdated && (
                            <p className="text-[10px] text-slate-400 font-bold mt-1 uppercase tracking-wider">
                                Last Synced: {new Date(lastUpdated).toLocaleString()}
                            </p>
                        )}
                    </div>
                    {syncMessage && (
                        <div
                            onClick={() => setSyncMessage(null)}
                            className="px-4 py-2 bg-blue-600 text-white text-[10px] font-black uppercase tracking-widest rounded-xl animate-in fade-in zoom-in slide-in-from-right-4 duration-300 cursor-pointer hover:bg-blue-700 transition-colors"
                            title="Click to dismiss"
                        >
                            {syncMessage}
                        </div>
                    )}
                </div>

                <div className="overflow-auto flex-1 custom-scrollbar">
                    {loading && data.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-24 text-slate-300">
                            <RefreshCw size={48} className="animate-spin mb-4" />
                            <p className="font-bold">Fetching Live Data...</p>
                        </div>
                    ) : data.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-24 text-slate-300">
                            <Table size={48} className="mb-4 opacity-20" />
                            <p className="font-bold">No records found.</p>
                        </div>
                    ) : (
                        <table className="w-full text-left border-collapse">
                            {/* For Playbooks with Body/Deep Dive, use custom collapsible rows */}
                            {isPlaybook && data.length > 0 && (data[0].Body || data[0].Native_Deep_Dive) ? (
                                <tbody>
                                    {data.map((row, idx) => (
                                        <CollapsiblePlaybookRow key={idx} row={row} rowIndex={idx} />
                                    ))}
                                </tbody>
                            ) : isActionPlan ? (
                                <tbody>
                                    {data.map((row, idx) => (
                                        <CollapsibleTaskRow
                                            key={idx}
                                            row={row}
                                            rowIndex={idx}
                                            onStatusChange={async (completed) => {
                                                const statusVal = completed ? 'Completed' : 'Pending';

                                                // Optimistic Update
                                                const newData = [...data];
                                                newData[idx] = { ...row, Status: statusVal };
                                                setData(newData);

                                                // Log to DB
                                                await db.logs.add({
                                                    code: tabName,
                                                    item: row.Task_Description || row.Task_ID || 'Unknown Task',
                                                    value: JSON.stringify({ Status: statusVal, Task_ID: row.Task_ID }),
                                                    timestamp: new Date().toISOString(),
                                                    status: 'pending',
                                                    remoteId: row.row_number,
                                                    targetClientEmail: targetClientEmail
                                                });
                                            }}
                                        />
                                    ))}
                                </tbody>
                            ) : (
                                <>
                                    <thead>
                                        <tr className="bg-slate-50/50">
                                            {(data.length > 0 && data[0] ? Object.keys(data[0]) : []).map((key) => (
                                                <th key={key} className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100">
                                                    {key}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-50">
                                        {data.map((row, idx) => (
                                            <tr
                                                key={idx}
                                                onClick={() => {
                                                    // Disable editing for protected tabs (Lessons / Tasks)
                                                    if (!isPlaybook && !isActionPlan) {
                                                        setEditingRow(row);
                                                    }
                                                }}
                                                className={`transition-colors group hover:bg-blue-50/50 cursor-pointer ${row._isPending ? 'bg-amber-50/50 italic opacity-80' : ''}`}
                                            >
                                                {Object.values(row).map((val, i) => {
                                                    const keys = Object.keys(row);
                                                    const key = keys[i];
                                                    const isStatus = key === 'Status' || key === 'Done';

                                                    if (isActionPlan && isStatus) {
                                                        const isChecked = val === 'Completed' || val === true || val === 'TRUE';
                                                        return (
                                                            <td key={i} className="px-6 py-4 text-sm font-bold text-slate-600">
                                                                <div className="flex items-center gap-2">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={isChecked}
                                                                        className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500 border-gray-300 cursor-pointer"
                                                                        onClick={(e) => e.stopPropagation()}
                                                                        onChange={async (e) => {
                                                                            const checked = e.target.checked;
                                                                            const statusVal = checked ? 'Completed' : 'Pending';

                                                                            // Optimistic Update
                                                                            const newData = [...data];
                                                                            newData[idx] = { ...row, [key]: statusVal };
                                                                            setData(newData);

                                                                            // Log to DB
                                                                            await db.logs.add({
                                                                                code: tabName,
                                                                                item: row.Item || row.Task_ID || 'Unknown Task',
                                                                                value: JSON.stringify({ [key]: statusVal, Task_ID: row.Task_ID }),
                                                                                timestamp: new Date().toISOString(),
                                                                                status: 'pending',
                                                                                remoteId: row.row_number,
                                                                                targetClientEmail: targetClientEmail
                                                                            });
                                                                        }}
                                                                    />
                                                                    <span className={isChecked ? "text-slate-400 text-xs uppercase font-bold" : "hidden"}>Done</span>
                                                                </div>
                                                            </td>
                                                        );
                                                    }

                                                    // 2. Formatted Text (All Tabs, notably Playbooks)
                                                    return (
                                                        <td key={i} className={`px-6 py-4 text-sm text-slate-600 ${isPlaybook ? 'leading-relaxed min-w-[300px]' : ''}`}>
                                                            {typeof val === 'string' && val.startsWith('http') ? (
                                                                <a href={val} target="_blank" rel="noreferrer" className="text-blue-600 underline" onClick={e => e.stopPropagation()}>Link</a>
                                                            ) : (
                                                                // Apply formatting intervention
                                                                <div className="whitespace-pre-wrap">{formatCellContent(val)}</div>
                                                            )}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        ))}
                                    </tbody>
                                </>
                            )}
                        </table>
                    )}
                </div>

                {/* Navigation Footer */}
                {(isPlaybook || isActionPlan) && onNavigate && (
                    <div className="p-6 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
                        <button
                            onClick={() => {
                                const container = document.querySelector('.custom-scrollbar'); // Try to find scroll container
                                if (container) {container.scrollTo({ top: 0, behavior: 'smooth' });}
                            }}
                            className="text-slate-400 hover:text-blue-600 text-[10px] font-black uppercase tracking-widest transition-colors"
                        >
                            Back to Top
                        </button>

                        {isPlaybook ? (
                            <button
                                onClick={() => onNavigate('My_Action_Plan')}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-2xl shadow-lg shadow-blue-200 active:scale-95 transition-all text-xs font-black uppercase tracking-widest flex items-center gap-2"
                            >
                                Go to My Action Plan <Zap size={14} className="fill-current" />
                            </button>
                        ) : (
                            <button
                                onClick={() => onNavigate('Playbooks')}
                                className="bg-white border border-slate-200 hover:bg-slate-50 text-slate-600 px-6 py-3 rounded-2xl active:scale-95 transition-all text-xs font-black uppercase tracking-widest flex items-center gap-2"
                            >
                                Go to Playbooks <FileSpreadsheet size={14} />
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* Row Edit Modal */}
            {editingRow && (
                <div className="fixed inset-0 z-[100] bg-slate-900/60 backdrop-blur-md flex items-end sm:items-center justify-center p-4 animate-in fade-in duration-300">
                    <div
                        className="bg-white rounded-[3rem] w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col shadow-2xl animate-in zoom-in-95 slide-in-from-bottom-10 duration-500"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Modal Header */}
                        <div className="p-10 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
                            <div>
                                <h3 className="text-xl font-black text-slate-800 uppercase tracking-tighter">Edit Record</h3>
                                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">Ref: {tabName.replace(/_/g, ' ')} • Row {editingRow.row_number}</p>
                            </div>
                            <button
                                onClick={() => setEditingRow(null)}
                                className="p-3 bg-white text-slate-400 hover:text-red-500 hover:rotate-90 rounded-2xl shadow-sm border border-slate-100 transition-all duration-300"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        {/* Modal Body - Form Fields */}
                        <div className="p-10 overflow-y-auto flex-1 space-y-8 custom-scrollbar">
                            {Object.entries(editingRow)
                                .filter(([key]) => !key.startsWith('_') && key !== 'row_number' && key !== 'Timestamp')
                                .map(([key, value]) => (
                                    <div key={key} className="group">
                                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 group-focus-within:text-blue-600 transition-colors">
                                            {key.replace(/_/g, ' ')}
                                        </label>
                                        <textarea
                                            className="w-full bg-slate-50 border-2 border-slate-100 rounded-2xl p-5 text-slate-800 font-medium focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 outline-none transition-all resize-none min-h-[100px]"
                                            value={value || ''}
                                            onChange={(e) => {
                                                setEditingRow(prev => ({ ...prev, [key]: e.target.value }));
                                            }}
                                            placeholder={`Enter ${key}...`}
                                        />
                                    </div>
                                ))}
                        </div>

                        {/* Modal Footer */}
                        <div className="p-10 bg-slate-50/80 border-t border-slate-100 flex gap-4">
                            <button
                                onClick={() => setEditingRow(null)}
                                className="flex-1 px-6 py-5 bg-white text-slate-500 font-black uppercase text-[10px] tracking-widest rounded-2xl border-2 border-slate-100 hover:bg-slate-100 transition-all active:scale-95"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={async () => {
                                    if (isSaving) {return;}
                                    setIsSaving(true);
                                    try {
                                        // 1. Log to Dexie
                                        await db.logs.add({
                                            code: tabName,
                                            item: editingRow.Item || editingRow.Task_ID || editingRow.Label || 'Record Update',
                                            value: JSON.stringify(editingRow),
                                            timestamp: new Date().toISOString(),
                                            status: 'pending',
                                            remoteId: editingRow.row_number,
                                            targetClientEmail: targetClientEmail
                                        });

                                        // 2. Optimistic Update in Local Data
                                        const newData = [...data];
                                        const idx = newData.findIndex(r => r.row_number === editingRow.row_number);
                                        if (idx !== -1) {
                                            newData[idx] = { ...editingRow, _isPending: true };
                                            setData(newData);
                                        }

                                        setEditingRow(null);
                                    } catch (e) {
                                        console.error("Save failed:", e);
                                    } finally {
                                        setIsSaving(false);
                                    }
                                }}
                                disabled={isSaving}
                                className="flex-[2] flex items-center justify-center gap-3 bg-slate-900 text-white font-black uppercase text-[10px] tracking-widest py-5 rounded-2xl hover:bg-blue-600 shadow-xl shadow-slate-900/20 transition-all active:scale-95 disabled:opacity-50"
                            >
                                {isSaving ? <RefreshCw size={16} className="animate-spin" /> : <Save size={16} />}
                                {isSaving ? 'Processing...' : 'Save Changes'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default NativeTabTable;
